import * as React from 'react';

import MoreOptionsIcon from '@mui/icons-material/MoreVert';
import AddSubsectionIcon from '@mui/icons-material/AddCircleOutline';
import SendToIcon from '@mui/icons-material/LowPriority';
import IncrementSortOrderIcon from '@mui/icons-material/ArrowDownward';
import DecrementSortOrderIcon from '@mui/icons-material/ArrowUpward';

import {
    Button
} from 'reactstrap';

import {
    Tooltip
} from '@mui/material';

import { LineItemHasMoreOptions } from './LineItemMoreOptions';

import { SanitizeDOMSelector } from '../../../Utils/DOMUtils';
import { PortalTextFinancialStatements } from '../../../Models/Api/strongbox.financialportal';

import { FSModels } from '@finagraph/financial-statement-editor';

interface MoreActionsMenuState {
    anchorElement: Element,
    lineItem: FSModels.FinancialStatementLineItemViewModel | undefined;
}

type Props = {
    lineItem: FSModels.FinancialStatementLineItemViewModel;
    detailAccountDescriptor: FSModels.DetailAccountDescriptor | undefined;
    onMoveLineItemToNewSection: (lineItem: FSModels.FinancialStatementLineItemViewModel) => void;
    onAddSubsection: (lineItem: FSModels.FinancialStatementLineItemViewModel) => void;
    onLineItemMoreOptions: (lineItem: FSModels.FinancialStatementLineItemViewModel, targetId: string) => void;
    onDecrementLineItemSortOrder: (lineItem: FSModels.FinancialStatementLineItemViewModel) => void;
    onIncrementLineItemSortOrder: (lineItem: FSModels.FinancialStatementLineItemViewModel) => void;
    showTooltips: boolean;
    isMultiSelection: boolean;
    financialStatementsText?: PortalTextFinancialStatements;
}

export const FinancialStatementLineItemActions: React.FC<Props> = (props): React.ReactElement => {
    const {
        lineItem,
        detailAccountDescriptor,
        onDecrementLineItemSortOrder,
        onIncrementLineItemSortOrder,
        onMoveLineItemToNewSection,
        onAddSubsection,
        onLineItemMoreOptions,
        showTooltips,
        isMultiSelection,
        financialStatementsText,
    } = props;

    const hasMoreOptions = React.useMemo(() => {
        return LineItemHasMoreOptions(lineItem);
    }, [lineItem])

    const moveActions: { moveUp: string; moveDown: string} = React.useMemo(() => {
        switch (lineItem.lineItemType) {
            case FSModels.FinancialStatementLineItemType.DetailAccount:
                return {
                    moveUp: financialStatementsText?.moveThisDetailAccountUp || '',
                    moveDown: financialStatementsText?.moveThisDetailAccountDown || '',
                };
            default:
                return {
                    moveUp: financialStatementsText?.moveThisClassificationUp || '',
                    moveDown: financialStatementsText?.moveThisClassificationDown || '',
                };
        }

        // This is the correct dependency list
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [lineItem])

    const allowMove = React.useMemo(() => {
        const sectionId = lineItem.sectionId.toLowerCase();

        return ((sectionId !== 'cash') && (sectionId !== 'accountsreceivable') && (sectionId !== 'accountspayable'));
    }, [lineItem]);

    return (
        <div className={'button-bar'}>
            {lineItem.canIncrementSortOrder && !isMultiSelection && (
                <Tooltip title={showTooltips ? moveActions.moveDown : ''}>
                    <Button
                        onClick={(e) => onIncrementLineItemSortOrder(lineItem)}
                        color="primary"
                    >
                        <IncrementSortOrderIcon />
                    </Button>
                </Tooltip>
            )}
            {(!lineItem.canIncrementSortOrder || isMultiSelection) && (
                <span className={'empty-action'}/>
            )}            
            {lineItem.canDecrementSortOrder &&!isMultiSelection && (
                <Tooltip title={showTooltips ? moveActions.moveUp : ''}>
                    <Button
                        onClick={(e) => onDecrementLineItemSortOrder(lineItem)}
                        color="primary"
                    >
                        <DecrementSortOrderIcon />
                    </Button>
                </Tooltip>
            )}
            {(!lineItem.canDecrementSortOrder || isMultiSelection) && (
                <span className={'empty-action'}/>
            )}
            {allowMove && (
                <Tooltip title={showTooltips ? (isMultiSelection ? (financialStatementsText?.sendLineItemsToDifferentLocation || '') : (financialStatementsText?.sendLineItemToDifferentLocation)) : ''}>
                    <Button
                        onClick={(e) => onMoveLineItemToNewSection(lineItem)}
                        color="primary"
                    >
                        <SendToIcon />
                    </Button>
                </Tooltip>
            )}
            {lineItem.canAddSubsection && !isMultiSelection && (
                <Tooltip title={showTooltips ? (financialStatementsText?.addDetailAccount || '') : ''}>
                    <Button
                        onClick={(e) => onAddSubsection(lineItem)}
                        color="primary"
                    >
                        <AddSubsectionIcon />
                    </Button>
                </Tooltip>
            )}
            {(!lineItem.canAddSubsection || isMultiSelection) && (
                <span className={'empty-action'}/>
            )}
            {hasMoreOptions && !isMultiSelection &&  (
                <React.Fragment>
                    <Tooltip title={showTooltips ? (financialStatementsText?.moreOptions || '') : ''}>
                        <Button
                            id={`financial-statement-line-item-more-options-${SanitizeDOMSelector(lineItem.id)}`}
                            onClick={e => onLineItemMoreOptions(lineItem, `financial-statement-line-item-more-options-${SanitizeDOMSelector(lineItem.id)}`)}
                            color="primary"
                        >
                            <MoreOptionsIcon/>
                        </Button>
                    </Tooltip>
                </React.Fragment>
            )}
            {(!hasMoreOptions || isMultiSelection) && (
                <span className={'empty-action'} />
            )}
            {(detailAccountDescriptor?.isNewlyReferenced ?? false) && (
                <Tooltip title={showTooltips ? (financialStatementsText?.accountDidntExistLastTime || '') : ''}>
                    <span className={'status-label status-label-green always-visible-item'}>New</span>
                </Tooltip>
            )}
            {!(detailAccountDescriptor?.isNewlyReferenced ?? false) && (
                <span className={'status-label-empty'} />
            )}
        </div>
    )
}
